import { useEffect, useRef, useState, useCallback } from "react";
import { X, Pen, Circle as CircleIcon, Square, MoveUpRight, Undo2, Eraser, Check } from "lucide-react";
import { useOverlayBackClose } from "@/hooks/backGuard";

/**
 * PhotoAnnotator — full-screen modal annotator.
 *
 * Tools: pen (freehand), arrow, circle, rectangle.
 * Colors: red (non-compliance default), yellow, white, black.
 * Strokes are stored in an undo-stack and redrawn on top of the original image.
 * Save → returns a new JPEG data URL via `onSave`.
 */
const COLORS = ["#EF4444", "#F59E0B", "#FFFFFF", "#0F172A"];
const TOOLS = [
  { id: "pen", label: "Pen", Icon: Pen },
  { id: "arrow", label: "Arrow", Icon: MoveUpRight },
  { id: "circle", label: "Circle", Icon: CircleIcon },
  { id: "rect", label: "Rect", Icon: Square },
];

const PhotoAnnotator = ({ open, src, onClose, onSave }) => {
  useOverlayBackClose(open, onClose);
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#EF4444");
  const [width, setWidth] = useState(6);
  const [strokes, setStrokes] = useState([]);    // committed
  const [current, setCurrent] = useState(null);  // in-progress

  // Load image when src changes
  useEffect(() => {
    if (!open || !src) return;
    setStrokes([]);
    setCurrent(null);
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => setImg(i);
    i.src = src;
  }, [open, src]);

  // Resize canvas to fit and draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !img || !wrap) return;
    const maxW = wrap.clientWidth;
    const maxH = wrap.clientHeight;
    const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
    const w = Math.floor(img.width * ratio);
    const h = Math.floor(img.height * ratio);
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    [...strokes, ...(current ? [current] : [])].forEach((s) => paintStroke(ctx, s));
  }, [img, strokes, current]);

  useEffect(() => { draw(); }, [draw]);
  useEffect(() => {
    const handler = () => draw();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [draw]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - r.left;
    const y = (t ? t.clientY : e.clientY) - r.top;
    return { x, y };
  };

  const onDown = (e) => {
    e.preventDefault();
    const p = getPos(e);
    setCurrent({ tool, color, width, points: [p] });
  };
  const onMove = (e) => {
    if (!current) return;
    e.preventDefault();
    const p = getPos(e);
    setCurrent((c) => {
      if (!c) return c;
      if (c.tool === "pen") return { ...c, points: [...c.points, p] };
      // shapes: keep first + current
      return { ...c, points: [c.points[0], p] };
    });
  };
  const onUp = () => {
    if (!current) return;
    setStrokes((s) => [...s, current]);
    setCurrent(null);
  };

  const undo = () => setStrokes((s) => s.slice(0, -1));
  const clearAll = () => setStrokes([]);

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL("image/jpeg", 0.88);
    onSave(data);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/95 flex flex-col" data-testid="photo-annotator">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-700 bg-slate-900 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {TOOLS.map(({ id, Icon, label }) => (
            <button
              key={id}
              data-testid={`annotator-tool-${id}`}
              onClick={() => setTool(id)}
              title={label}
              className={`h-10 px-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${
                tool === id ? "bg-white text-slate-900 border-white" : "bg-transparent text-white border-slate-700 hover:border-white"
              }`}
            >
              <Icon size={16} strokeWidth={2.5} />{label}
            </button>
          ))}
          <div className="h-8 w-px bg-slate-700 mx-1" />
          {COLORS.map((c) => (
            <button
              key={c}
              data-testid={`annotator-color-${c.replace("#","").toLowerCase()}`}
              onClick={() => setColor(c)}
              style={{ background: c }}
              className={`w-9 h-9 border-2 transition-transform ${color === c ? "border-white scale-110" : "border-slate-700"}`}
              aria-label={`Color ${c}`}
            />
          ))}
          <div className="h-8 w-px bg-slate-700 mx-1" />
          <label className="inline-flex items-center gap-2 text-white text-xs font-bold">
            <span className="uppercase tracking-wider">Size</span>
            <input
              data-testid="annotator-width"
              type="range" min="2" max="16" value={width}
              onChange={(e) => setWidth(Number(e.target.value))}
              className="w-24 accent-white"
            />
            <span className="tabular-nums w-6 text-right">{width}</span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={undo} disabled={!strokes.length} data-testid="annotator-undo"
            className="h-10 px-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-transparent text-white border-2 border-slate-700 hover:border-white disabled:opacity-40">
            <Undo2 size={16} strokeWidth={2.5}/>Undo
          </button>
          <button onClick={clearAll} disabled={!strokes.length} data-testid="annotator-clear"
            className="h-10 px-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-transparent text-white border-2 border-slate-700 hover:border-white disabled:opacity-40">
            <Eraser size={16} strokeWidth={2.5}/>Clear
          </button>
          <button onClick={onClose} data-testid="annotator-cancel"
            className="h-10 px-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-transparent text-white border-2 border-slate-700 hover:border-white">
            <X size={16} strokeWidth={2.5}/>Cancel
          </button>
          <button onClick={save} data-testid="annotator-save"
            className="h-10 px-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide bg-emerald-500 text-white border-2 border-emerald-500 hover:bg-emerald-600">
            <Check size={16} strokeWidth={2.5}/>Save
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div ref={wrapRef} className="flex-1 flex items-center justify-center overflow-hidden p-2">
        {img ? (
          <canvas
            ref={canvasRef}
            data-testid="annotator-canvas"
            className="shadow-2xl touch-none cursor-crosshair bg-slate-800 select-none"
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
          />
        ) : (
          <div className="text-white text-sm">Loading…</div>
        )}
      </div>

      <p className="text-center text-[11px] text-slate-400 py-2 px-4 border-t border-slate-700">
        Tip: Use <strong className="text-white">red</strong> for non-compliances, <strong className="text-white">yellow</strong> to highlight measurements, and the <strong className="text-white">arrow</strong> tool to point at specific issues.
      </p>
    </div>
  );
};

function paintStroke(ctx, s) {
  ctx.save();
  ctx.strokeStyle = s.color;
  ctx.lineWidth = s.width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.fillStyle = s.color;
  const pts = s.points;
  if (!pts || !pts.length) { ctx.restore(); return; }

  if (s.tool === "pen") {
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.stroke();
  } else if (pts.length >= 2) {
    const a = pts[0], b = pts[pts.length - 1];
    if (s.tool === "rect") {
      const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
      const w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
      ctx.strokeRect(x, y, w, h);
    } else if (s.tool === "circle") {
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      const rx = Math.abs(b.x - a.x) / 2;
      const ry = Math.abs(b.y - a.y) / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.tool === "arrow") {
      const dx = b.x - a.x, dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1) { ctx.restore(); return; }
      const head = Math.max(12, s.width * 3);
      const ang = Math.atan2(dy, dx);
      // shaft
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x - Math.cos(ang) * head * 0.6, b.y - Math.sin(ang) * head * 0.6);
      ctx.stroke();
      // head
      ctx.beginPath();
      ctx.moveTo(b.x, b.y);
      ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 7),
                 b.y - head * Math.sin(ang - Math.PI / 7));
      ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 7),
                 b.y - head * Math.sin(ang + Math.PI / 7));
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
}

export default PhotoAnnotator;
